import React, { useRef } from 'react';
import { SectionProps } from '../types/SectionProps';
import { Box, Container, Typography, Button, TextField, Paper, Stack } from '@mui/material';
import { motion, useInView } from 'framer-motion';

const CtaSection: React.FC<SectionProps> = ({ id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Ici, vous pourriez ajouter la logique d'envoi du formulaire
    console.log('Formulaire soumis');
  };

  return (
    <Box
      id={id}
      ref={ref}
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'secondary.main',
        color: 'primary.main',
        scrollMarginTop: '64px', // Pour le défilement fluide
      }}
    >
      {/* Accent fuchsia en haut de la section */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '5px',
          bgcolor: 'error.main',
        }}
      />
      
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} alignItems="center">
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.6 }}
            >
              <Typography 
                variant="h2" 
                component="h2" 
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  position: 'relative',
                  display: 'inline-block'
                }}
              >
                Prenez rendez-vous avec Armelle
                <Box 
                  sx={{ 
                    position: 'absolute',
                    bottom: -10,
                    left: 0,
                    width: '40px',
                    height: '3px',
                    bgcolor: 'error.main'
                  }} 
                />
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: '500px'
                }}
              >
                Parce qu'au-delà de la transaction, je privilégie la relation humaine.
                Travailler avec moi, c'est bénéficier d'un accompagnement sur-mesure, 
                humain et transparent.
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Armelle Bonaventure
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Conseillère indépendante en immobilier et gestion patrimoniale
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  📧 <a href="mailto:abonaventure@hotmail.fr" style={{ color: '#E63976', textDecoration: 'none' }}>
                    abonaventure@hotmail.fr
                  </a>
                </Typography>
                <Typography variant="body1">
                  📞 <a href="tel:+33670102250" style={{ color: '#1E2A38', textDecoration: 'none' }}>
                    +33 6 70 10 22 50
                  </a>
                </Typography>
                <Typography variant="body1">
                  📍 Route des Camoins, 13011 Marseille
                </Typography>
              </Box>
            </motion.div>
          </Box>
          
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4,
                  borderRadius: '4px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  border: '1px solid',
                  borderColor: 'background.default'
                }}
              >
                <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 600 }}>
                  Contactez-moi
                </Typography>
                
                <form onSubmit={handleSubmit}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Nom"
                        variant="outlined"
                        required
                      />
                      <TextField
                        fullWidth
                        label="Prénom"
                        variant="outlined"
                        required
                      />
                    </Stack>
                    <TextField
                      fullWidth
                      label="Email"
                      variant="outlined"
                      type="email"
                      required
                    />
                    <TextField
                      fullWidth
                      label="Téléphone"
                      variant="outlined"
                    />
                    <TextField
                      fullWidth
                      label="Message"
                      variant="outlined"
                      multiline
                      rows={4}
                      required
                      sx={{ mb: 2 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      fullWidth
                      size="large"
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        borderRadius: '2px',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: 'none',
                          bgcolor: '#d32f6c',
                        }
                      }}
                    >
                      Envoyer ma demande
                    </Button>
                  </Stack>
                </form>
              </Paper>
            </motion.div>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default CtaSection;
